import {
  Component,
  Input,
  OnDestroy,
  ElementRef,
  AfterViewInit,
  OnChanges,
  OnInit,
  ChangeDetectorRef,
  ChangeDetectionStrategy
} from '@angular/core';
import { UIComponentsModule } from 'src/app/shared/ui-components/ui-components.module';
import * as d3 from 'd3';
import {
  ALL_CATEGORIES,
  BASELINE_CATEGORIES,
  DAILY_CATEGORIES,
  getCategoryColor,
  generateYTicks,
  processComparisonData,
  calculateBaselineEmissions,
  calculateDailyEmissions,
} from 'src/app/shared/charts/chart-data.util';
import {
  CategoryData,
  ComparisonData,
  EmissionsData,
  EmissionsReport,
} from 'src/app/shared/models/interfaces';

@Component({
  selector: 'app-emissions-chart',
  imports: [UIComponentsModule],
  templateUrl: './emissions-chart.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './emissions-chart.component.scss',
})
export class EmissionsChartComponent
  implements OnInit, OnDestroy, AfterViewInit, OnChanges
{
  @Input() reportData!: EmissionsReport;

  private svg: any;
  private comparisonSvg: any;
  private margin = { top: 20, right: 30, bottom: 60, left: 60 };
  private width: number = 600;
  private height: number = 600;
  private resizeObserver: ResizeObserver;

  displayedDates: Date[] = [];
  visibleCategories = new Set<string>(ALL_CATEGORIES.map((c) => c.key));
  comparisonData: ComparisonData[] = [];

  // category groups for filter/legend display
  baselineCategories = BASELINE_CATEGORIES;
  dailyCategories = DAILY_CATEGORIES;

  isReportDataAvailable: boolean = false;

  private _tooltipTimeout: any;

  constructor(private elementRef: ElementRef, private cdr: ChangeDetectorRef) {
    this.resizeObserver = new ResizeObserver(() => {
      this.generateChart();
      this.generateComparisonChart();
    });
  }

  ngOnInit(): void {
    this.checkReportDataAvailability();
  }

  ngOnChanges(): void {
    this.checkReportDataAvailability();
    this.updateComparisonData();
    this.cdr.detectChanges();
    this.generateChart();
    this.generateComparisonChart();
  }

  ngAfterViewInit(): void {
    const container =
      this.elementRef.nativeElement.querySelector('.emissions-chart');
    const comparisonContainer =
      this.elementRef.nativeElement.querySelector('.comparison-chart');
    if (container) {
      this.resizeObserver.observe(container);
    }
    if (comparisonContainer) {
      this.resizeObserver.observe(comparisonContainer);
    }
  }

  ngOnDestroy(): void {
    if (this.svg) {
      this.svg.remove();
    }
    if (this.comparisonSvg) {
      this.comparisonSvg.remove();
    }
    this.resizeObserver.disconnect();
  }

  checkReportDataAvailability() {
    const wasAvailable = this.isReportDataAvailable;
    this.isReportDataAvailable =
      !!this.reportData &&
      (this.reportData.activity.length > 0 ||
        this.reportData.baseline.length > 0 ||
        this.reportData.food.length > 0 ||
        this.reportData.flight.length > 0 ||
        this.reportData.mobility.length > 0 ||
        this.reportData.product.length > 0 ||
        this.reportData.constants.length > 0);

    // Only trigger change detection if the value actually changed
    if (wasAvailable !== this.isReportDataAvailable) {
      this.cdr.markForCheck();
    }
  }

  onDatesGenerated(dates: Date[]): void {
    this.displayedDates = dates;
    this.generateChart();
  }

  toggleCategory(categoryKey: string, event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target.checked) {
      this.visibleCategories.add(categoryKey);
    } else {
      this.visibleCategories.delete(categoryKey);
    }

    this.generateChart();
    this.generateComparisonChart(); // Add this line to update comparison chart on filter change
  }

  private updateDimensions(container?: any): void {
    const targetContainer =
      container ||
      this.elementRef.nativeElement.querySelector('.emissions-chart');
    if (targetContainer) {
      const containerWidth = targetContainer.clientWidth;
      const containerHeight = targetContainer.clientHeight;
      const baseLeftMargin = containerWidth < 768 ? 40 : 60;

      this.margin.left = baseLeftMargin;
      this.width = containerWidth - this.margin.left - this.margin.right;
      this.height = containerHeight - this.margin.top - this.margin.bottom;
    }
  }

  private generateChart(): void {
    const container =
      this.elementRef.nativeElement.querySelector('.emissions-chart');

    if (
      !container ||
      !this.reportData ||
      !this.displayedDates.length ||
      !this.isReportDataAvailable
    ) {
      return;
    }

    const chartData = this.processReportData();
    this.createChart(chartData, container);
  }

  private updateComparisonData(): void {
    if (this.reportData) {
      this.comparisonData = processComparisonData(
        this.reportData.groupAverages,
        this.reportData.platformAverages,
        this.reportData
      );
    } else {
      this.comparisonData = [];
    }
  }

  private generateComparisonChart(): void {
    const container =
      this.elementRef.nativeElement.querySelector('.comparison-chart');
    if (!container || this.comparisonData.length === 0) return;

    this.createComparisonChart(this.comparisonData, container);
  }

  private createChart(chartData: EmissionsData[], container: any): void {
    d3.select(container).selectAll('*').remove();
    this.updateDimensions(container);

    const minBarWidth = 60;
    const minChartWidth = Math.max(
      this.width,
      chartData.length * minBarWidth + this.margin.left + this.margin.right
    );

    this.svg = d3
      .select(container)
      .append('svg')
      .attr('width', minChartWidth)
      .attr('height', this.height + this.margin.top + this.margin.bottom)
      .attr(
        'viewBox',
        `0 0 ${minChartWidth} ${
          this.height + this.margin.top + this.margin.bottom
        }`
      )
      .attr('preserveAspectRatio', 'xMidYMid meet');

    const g = this.svg
      .append('g')
      .attr('transform', `translate(${this.margin.left},${this.margin.top})`);

    const chartWidth = minChartWidth - this.margin.left - this.margin.right;

    const xScale = d3
      .scaleBand()
      .domain(chartData.map((d) => d.dateString))
      .range([20, chartWidth])
      .padding(0.2);

    // calculate y-axis domain dynamically based on visible data
    const maxEmissions = d3.max(chartData, (d) => d.totalEmissions) || 5;

    const yAxisMax = this.calculateYAxisMax(maxEmissions);

    const yDomain: [number, number] = [0, yAxisMax];

    const yScale = d3.scaleLinear().domain(yDomain).range([this.height, 0]);

    this.createBars(g, chartData, xScale, yScale);
    this.createAxes(g, xScale, yScale, yDomain);
  }

  private createComparisonChart(
    comparisonData: ComparisonData[],
    container: any
  ): void {
    d3.select(container).selectAll('*').remove();
    this.updateDimensions(container);

    const minBarWidth = 120;
    const maxBarWidth = 200;
    const minChartWidth = Math.max(
      this.width,
      comparisonData.length * minBarWidth + this.margin.left + this.margin.right
    );

    this.comparisonSvg = d3
      .select(container)
      .append('svg')
      .attr('width', minChartWidth)
      .attr('height', this.height + this.margin.top + this.margin.bottom)
      .attr(
        'viewBox',
        `0 0 ${minChartWidth} ${
          this.height + this.margin.top + this.margin.bottom
        }`
      )
      .attr('preserveAspectRatio', 'xMidYMid meet');

    const g = this.comparisonSvg
      .append('g')
      .attr('transform', `translate(${this.margin.left},${this.margin.top})`);

    const chartWidth = minChartWidth - this.margin.left - this.margin.right;

    const xScale = d3
      .scaleBand()
      .domain(comparisonData.map((d) => d.label))
      .range([20, chartWidth])
      .padding(0.3);

    // Filter comparison data based on visible categories
    const filteredComparisonData = comparisonData.map((d) => ({
      ...d,
      categories: d.categories.filter((cat) =>
        this.visibleCategories.has(cat.key)
      ),
      totalEmissions:
        d.label === 'Target Value'
          ? d.totalEmissions
          : d.categories
              .filter((cat) => this.visibleCategories.has(cat.key))
              .reduce((sum, cat) => sum + cat.emissions, 0),
    }));

    const maxEmissions =
      d3.max(filteredComparisonData, (d) => d.totalEmissions) || 5;

    const yAxisMax = this.calculateYAxisMax(maxEmissions);

    const yScale = d3
      .scaleLinear()
      .domain([0, yAxisMax])
      .range([this.height, 0]);

    this.createComparisonBars(g, filteredComparisonData, xScale, yScale);
    this.createComparisonAxes(g, xScale, yScale);
  }

  private createComparisonBars(
    g: any,
    comparisonData: ComparisonData[],
    xScale: any,
    yScale: any
  ): void {
    const defs = this.comparisonSvg.append('defs');

    // create clipping paths for rounded bar corners
    comparisonData.forEach((d, i) => {
      const barWidth = Math.max(xScale.bandwidth(), 40);
      const barHeight = Math.max(0, this.height - yScale(d.totalEmissions));
      const xPos = xScale(d.label)!;
      const yPos = yScale(d.totalEmissions);

      if (barHeight > 0) {
        defs
          .append('clipPath')
          .attr('id', `comparison-rounded-corner-${i}`)
          .append('rect')
          .attr('x', xPos)
          .attr('y', yPos)
          .attr('width', barWidth)
          .attr('height', barHeight)
          .attr('rx', 5)
          .attr('ry', 5);
      }
    });

    // Get tooltip element
    const tooltip = d3.select('.tooltip');

    comparisonData.forEach((d, i) => {
      const barWidth = xScale.bandwidth();
      const xPos = xScale(d.label)!;

      if (d.label === 'Target Value') {
        // single color bar for target value
        g.append('rect')
          .attr('x', xPos)
          .attr('y', yScale(d.totalEmissions))
          .attr('width', barWidth)
          .attr('height', this.height - yScale(d.totalEmissions))
          .attr('fill', 'var(--accent)')
          .attr('clip-path', `url(#comparison-rounded-corner-${i})`)
          .attr('category-value', d.totalEmissions.toFixed(2))
          .attr('category-label', 'Target Value')
          .on('mouseover', (event: any) => {
            const categoryValue = event.target.getAttribute('category-value');
            const categoryLabel = event.target.getAttribute('category-label');
            tooltip
              .html(
                `<strong>${categoryLabel}</strong>:<br> ${categoryValue} kg CO₂`
              )
              .style('left', `${event.pageX}px`)
              .style('top', `${event.pageY}px`)
              .style('opacity', 0.75);
          })
          .on('mousemove', (event: any) => {
            clearTimeout(this._tooltipTimeout);
            this._tooltipTimeout = setTimeout(() => {
              tooltip
                .style('left', `${event.pageX}px`)
                .style('top', `${event.pageY}px`);
            }, 10);
          })
          .on('mouseout', () => {
            tooltip.style('opacity', 0);
          });
      } else {
        // create stacked bars using d3 stack
        const stack = d3
          .stack<ComparisonData>()
          .keys(
            ALL_CATEGORIES.filter((c) => this.visibleCategories.has(c.key)).map(
              (c) => c.key
            )
          )
          .value(
            (datum, key) =>
              datum.categories.find((c) => c.key === key)?.emissions || 0
          );

        const stackedData = stack([d]);

        stackedData.forEach((categoryStack) => {
          categoryStack.forEach((segment) => {
            const segmentHeight = Math.max(
              0,
              yScale(segment[0]) - yScale(segment[1])
            );
            if (segmentHeight > 0) {
              const categoryEmissions = segment[1] - segment[0];
              const category = ALL_CATEGORIES.find(
                (c) => c.key === categoryStack.key
              );

              g.append('rect')
                .attr('x', xPos)
                .attr('y', yScale(segment[1]))
                .attr('width', barWidth)
                .attr('height', segmentHeight)
                .attr('fill', getCategoryColor(categoryStack.key))
                .attr('clip-path', `url(#comparison-rounded-corner-${i})`)
                .attr('category-value', categoryEmissions.toFixed(2))
                .attr('category-label', category?.label || categoryStack.key)
                .on('mouseover', (event: any) => {
                  const categoryValue =
                    event.target.getAttribute('category-value');
                  const categoryLabel =
                    event.target.getAttribute('category-label');
                  tooltip
                    .html(
                      `<strong>${categoryLabel}</strong>:<br> ${categoryValue} kg CO₂`
                    )
                    .style('left', `${event.pageX}px`)
                    .style('top', `${event.pageY}px`)
                    .style('opacity', 0.75);
                })
                .on('mousemove', (event: any) => {
                  clearTimeout(this._tooltipTimeout);
                  this._tooltipTimeout = setTimeout(() => {
                    tooltip
                      .style('left', `${event.pageX}px`)
                      .style('top', `${event.pageY}px`);
                  }, 10);
                })
                .on('mouseout', () => {
                  tooltip.style('opacity', 0);
                });
            }
          });
        });
      }
    });
  }

  private createComparisonAxes(g: any, xScale: any, yScale: any): void {
    // x-axis
    const xAxis = g
      .append('g')
      .attr('transform', `translate(0,${this.height})`)
      .call(d3.axisBottom(xScale));

    xAxis.selectAll('path, line').remove();
    xAxis.selectAll('.tick text').attr('class', 'x-axis-label');

    // y-axis with simplified tick generation
    const yTicks = generateYTicks(0, yScale.domain()[1]);
    g.append('g')
      .call(
        d3
          .axisLeft(yScale)
          .tickValues(yTicks)
          .tickFormat((d) => `${d}kg CO₂`)
          .tickSize(0)
      )
      .selectAll('path, line')
      .remove();
  }

  private createBars(
    g: any,
    chartData: EmissionsData[],
    xScale: any,
    yScale: any
  ): void {
    const defs = this.svg.append('defs');

    // create clipping paths for rounded bar corners
    chartData.forEach((d, i) => {
      const barWidth = Math.max(xScale.bandwidth(), 40);
      const barHeight = Math.max(0, this.height - yScale(d.totalEmissions));
      const xPos = xScale(d.dateString)!;
      const yPos = yScale(d.totalEmissions);

      if (barHeight > 0) {
        defs
          .append('clipPath')
          .attr('id', `rounded-corner-${i}`)
          .append('rect')
          .attr('x', xPos)
          .attr('y', yPos)
          .attr('width', barWidth)
          .attr('height', barHeight)
          .attr('rx', 5)
          .attr('ry', 5);
      }
    });

    // create stacked bar data using d3 stack generator
    const stack = d3
      .stack<EmissionsData>()
      .keys(ALL_CATEGORIES.map((c) => c.key))
      .value(
        (d, key) => d.categories.find((c: any) => c.key === key)?.emissions || 0
      );

    const stackedData = stack(chartData);

    // search tooltip
    const tooltip = d3.select('.tooltip');

    g.selectAll('.category-group')
      .data(stackedData)
      .enter()
      .append('g')
      .attr('class', 'category-group')
      .attr('fill', (d: any) => getCategoryColor(d.key))
      .selectAll('.bar')
      .data((d: any) => d)
      .enter()
      .append('rect')
      .attr('class', 'bar')
      .attr('x', (d: any) => xScale(d.data.dateString)!)
      .attr('y', (d: any) => Math.max(0, Math.min(yScale(d[1]), this.height)))
      .attr('width', Math.max(xScale.bandwidth(), 40))
      .attr('height', (d: any) => {
        const y0 = Math.max(0, Math.min(yScale(d[0]), this.height));
        const y1 = Math.max(0, Math.min(yScale(d[1]), this.height));
        return Math.max(0, y0 - y1);
      })
      .attr('clip-path', (d: any) => {
        const dataIndex = chartData.findIndex(
          (data) => data.dateString === d.data.dateString
        );
        return `url(#rounded-corner-${dataIndex})`;
      })
      .attr('category-value', (d: any) => {
        return (d[1] - d[0]).toFixed(2);
      })
      .attr('category-label', (d: any, i: any, nodes: any) => {
        const parentData = d3.select(nodes[i].parentNode).datum() as any;
        const category = ALL_CATEGORIES.find((c) => c.key === parentData.key);
        return category?.label || parentData.key;
      })

      .on('mouseover', (event: any) => {
        const categoryValue = event.target.getAttribute('category-value');
        const categoryLabel = event.target.getAttribute('category-label');
        tooltip
          .html(
            `<strong>${categoryLabel}</strong>:<br> ${categoryValue} kg CO₂`
          )
          .style('left', `${event.pageX}px`)
          .style('top', `${event.pageY}px`)
          .style('opacity', 0.75);
      })
      .on('mousemove', (event: any) => {
        clearTimeout(this._tooltipTimeout);
        this._tooltipTimeout = setTimeout(() => {
          tooltip
            .style('left', `${event.pageX}px`)
            .style('top', `${event.pageY}px`);
        }, 10);
      })
      .on('mouseout', () => {
        tooltip.style('opacity', 0);
      });
  }

  private createAxes(
    g: any,
    xScale: any,
    yScale: any,
    yDomain: [number, number]
  ): void {
    // y-axis first (background)
    const yTicks = this.generateYTicks(yDomain[0], yDomain[1]);
    g.append('g')
      .attr('class', 'y-axis')
      .call(
        d3
          .axisLeft(yScale)
          .tickValues(yTicks)
          .tickFormat((d) => `${d}kg CO₂`)
          .tickSize(0)
      )
      .selectAll('path, line')
      .remove();

    // x-axis on top (foreground) - positioned exactly at chart bottom
    const xAxis = g
      .append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0,${this.height})`)
      .call(d3.axisBottom(xScale));

    xAxis.selectAll('path, line').remove();
    xAxis
      .selectAll('.tick text')
      .attr('class', 'x-axis-label')
      .style('text-anchor', 'middle');
  }

  // generate appropriate y-axis tick marks that naturally cover the domain
  private generateYTicks(min: number, max: number): number[] {
    const targetTickCount = Math.max(4, Math.floor(this.height / 100));
    return d3.ticks(min, max, targetTickCount);
  }

  private calculateYAxisMax(maxEmissions: number): number {
    let yAxisMax: number;
    if (maxEmissions <= 2) {
      yAxisMax = Math.ceil(maxEmissions * 2) / 2; // Rundet auf 0.5er-Schritte
    } else if (maxEmissions <= 10) {
      yAxisMax = Math.ceil(maxEmissions); // Ganze Zahlen
    } else {
      // Für größere Werte auf 5er-Schritte runden
      yAxisMax = Math.ceil(maxEmissions / 5) * 5;
    }
    return yAxisMax;
  }
  private processReportData(): EmissionsData[] {
    if (!this.reportData || !this.displayedDates) return [];

    // calculate baseline emissions for daily emissions equivalent (yearly data divided by 365)
    const baselineEmissions = calculateBaselineEmissions(this.reportData);
    const dailyEmissionsMap = new Map<string, Record<string, number>>();

    // pre-calculate daily emissions for all dates for performance
    this.displayedDates.forEach((date) => {
      dailyEmissionsMap.set(
        date.toDateString(),
        calculateDailyEmissions(this.reportData, date)
      );
    });

    return this.displayedDates.map((date) => {
      const dateString = date.toLocaleDateString('de-CH', {
        day: 'numeric',
        month: 'short',
      });
      const dailyEmissions = dailyEmissionsMap.get(date.toDateString()) || {};

      // combine baseline and daily emissions based on visibility filters
      const categories: CategoryData[] = ALL_CATEGORIES.map((category) => ({
        key: category.key,
        emissions: this.visibleCategories.has(category.key)
          ? category.key.startsWith('baseline')
            ? baselineEmissions[category.key] || 0
            : dailyEmissions[category.key] || 0
          : 0,
        label: category.label,
        color: category.color,
      }));

      return {
        dateString,
        date,
        categories,
        totalEmissions: categories.reduce((sum, cat) => sum + cat.emissions, 0),
      };
    });
  }

  downloadReport(): void {
    const csvContent = this.generateCSVReport();
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `mmp-report-${new Date().toISOString().split('T')[0]}.csv`
    );
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  generateCSVReport(): string {
    if (!this.displayedDates.length) return '';

    const chartData = this.processReportData();

    // CSV header with proper escaping
    const headers = ['Date', 'Total Emissions (kg CO2)'];
    ALL_CATEGORIES.forEach((category) => {
      if (this.visibleCategories.has(category.key)) {
        headers.push(`${category.label} (kg CO2)`);
      }
    });

    // CSV rows with proper formatting
    const rows = chartData.map((dataPoint) => {
      const row = [
        `"${dataPoint.dateString}"`,
        dataPoint.totalEmissions.toFixed(2),
      ];

      ALL_CATEGORIES.forEach((category) => {
        if (this.visibleCategories.has(category.key)) {
          const categoryData = dataPoint.categories.find(
            (c) => c.key === category.key
          );
          row.push((categoryData?.emissions || 0).toFixed(2));
        }
      });

      return row.join(',');
    });

    // Use comma delimiter and add BOM for proper Excel recognition
    const csvContent = '\uFEFF' + [headers.join(','), ...rows].join('\n');
    return csvContent;
  }
}
